/**
 * TempHP, FastHealing, LoseHitPoints and SpecialResource (Foundry: rule-element/temp-hp.ts,
 * fast-healing.ts, lose-hit-points.ts, special-resource.ts): what the resources block shows and
 * what the Session (#18) applies: temporary hit points on arrival, healing at the start of a
 * turn, hit points lost, and counted resources such as versatile vials.
 */
import { localize } from "../labels";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { sluggify, sluggifyCamel } from "../slug";
import type { EngineState } from "../state";
import type { RuleHandler, RuleInstance } from "./base";

/* ----------------------------------- TempHP ----------------------------------- */

interface TempHPData {
  value: unknown;
  events: { onCreate: boolean; onTurnStart: boolean };
}

function parseTempHP(rule: RuleInstance): void {
  const s = rule.source;
  const value = cleanRuleValue(s.value);
  if (value === undefined) return rule.fail("value: may not be undefined");
  const events = typeof s.events === "object" && s.events !== null ? (s.events as Record<string, unknown>) : {};
  rule.data = { value, events: { onCreate: events.onCreate !== false, onTurnStart: events.onTurnStart === true } } satisfies TempHPData as unknown as Record<string, unknown>;
}

function afterTempHP(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as TempHPData;
  const enabled = rule.test(state);
  const value = Math.trunc(Number(resolveValue(rule, state, data.value)));
  if (rule.ignored) return;
  if (Number.isNaN(value) || value < 0) return rule.fail("value: must resolve to a positive number");
  state.synthetics.tempHP.push({ value, events: { ...data.events }, predicate: structuredClone(rule.predicate), enabled, source: rule.item.uuid, sourceName: rule.item.name });
}

export const TempHPHandler: RuleHandler = { key: "TempHP", parse: parseTempHP, afterPrepareData: afterTempHP };

/* --------------------------------- FastHealing --------------------------------- */

interface FastHealingData {
  value: unknown;
  type: "fast-healing" | "regeneration";
  details: string | null;
  deactivatedBy: string[];
}

function parseFastHealing(rule: RuleInstance, state: EngineState): void {
  const s = rule.source;
  const value = cleanRuleValue(s.value);
  if (value === undefined) return rule.fail("value: may not be undefined");
  const type = s.type === undefined ? "fast-healing" : s.type;
  if (type !== "fast-healing" && type !== "regeneration") return rule.fail(`type: "${String(type)}" is not fast-healing or regeneration`);
  const deactivatedBy = Array.isArray(s.deactivatedBy) ? s.deactivatedBy.filter((t): t is string => typeof t === "string" && !!t) : [];
  let details = typeof s.details === "string" && s.details ? (state.catalogue?.localize(s.details) ?? s.details) : null;
  if (type === "fast-healing" && s.deactivatedBy !== undefined) return rule.fail("deactivatedBy is only valid for type regeneration");
  if (type === "regeneration" && details) return rule.fail("details is only valid for type fast-healing");
  if (type === "regeneration" && deactivatedBy.length > 0) {
    const table = state.catalogue?.table("weaknessTypes");
    const names = deactivatedBy.map((t) => table?.[t] ?? t);
    const list = names.length <= 1 ? (names[0] ?? "") : names.length === 2 ? `${names[0]} or ${names[1]}` : `${names.slice(0, -1).join(", ")}, or ${names[names.length - 1]}`;
    details = localize(state, "PF2E.Encounter.Broadcast.FastHealing.DeactivatedBy", "Deactivated by {types}", { types: list });
  }
  rule.data = { value, type, details, deactivatedBy } satisfies FastHealingData as unknown as Record<string, unknown>;
}

function afterFastHealing(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as FastHealingData;
  const enabled = rule.test(state);
  const value = resolveValue(rule, state, data.value);
  if (rule.ignored) return;
  if (typeof value !== "number" && typeof value !== "string") return rule.fail("value must be a number or a roll formula");
  state.synthetics.fastHealing.push({ type: data.type, value, details: data.details ?? rule.reducedLabel, deactivatedBy: [...data.deactivatedBy], predicate: structuredClone(rule.predicate), enabled, source: rule.item.uuid, sourceName: rule.item.name });
}

export const FastHealingHandler: RuleHandler = { key: "FastHealing", parse: parseFastHealing, afterPrepareData: afterFastHealing };

/* -------------------------------- LoseHitPoints -------------------------------- */

interface LoseHitPointsData {
  value: unknown;
  recoverable: boolean;
  reevaluateOnUpdate: boolean;
}

function parseLoseHitPoints(rule: RuleInstance): void {
  const s = rule.source;
  const value = cleanRuleValue(s.value);
  if (value === undefined) return rule.fail("value: may not be undefined");
  rule.data = { value, recoverable: s.recoverable !== false, reevaluateOnUpdate: s.reevaluateOnUpdate === true } satisfies LoseHitPointsData as unknown as Record<string, unknown>;
}

function beforeLoseHitPoints(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as LoseHitPointsData;
  const value = Math.trunc(Math.abs(Number(resolveValue(rule, state, data.value)) || 0));
  if (rule.ignored) return;
  if (!data.recoverable) state.actor.system.attributes.hp.unrecoverable += value;
  state.synthetics.loseHitPoints.push({ value, recoverable: data.recoverable, source: rule.item.uuid, sourceName: rule.item.name });
}

export const LoseHitPointsHandler: RuleHandler = { key: "LoseHitPoints", parse: parseLoseHitPoints, beforePrepareData: beforeLoseHitPoints };

/* -------------------------------- SpecialResource -------------------------------- */

interface SpecialResourceData {
  slug: string;
  value: number | null;
  max: unknown;
  itemUuid: string | null;
  level: unknown;
  renew: "daily" | false;
}

const INVALID_RESOURCES = ["heroPoints", "focus", "investiture", "mythicPoints", "crafting", "infusedReagents", "hero-points", "mythic-points", "infused-reagents"];

function parseSpecialResource(rule: RuleInstance): void {
  const s = rule.source;
  const slug = rule.slug ?? rule.item.slug ?? sluggify(rule.item.name);
  if (INVALID_RESOURCES.includes(slug)) return rule.fail("slug: invalid value");
  const max = cleanRuleValue(s.max);
  if (max === undefined) return rule.fail("max: may not be undefined");
  const itemUuid = typeof s.itemUUID === "string" && s.itemUUID ? s.itemUUID : null;
  if (s.level !== undefined && s.level !== null && !itemUuid) return rule.fail("level can only be set if itemUUID is set");
  if (typeof s.priority !== "number") rule.priority = 18;
  rule.slug = slug;
  rule.data = { slug, value: typeof s.value === "number" ? s.value : null, max, itemUuid, level: s.level === undefined ? null : cleanRuleValue(s.level, true), renew: s.renew === false ? false : "daily" } satisfies SpecialResourceData as unknown as Record<string, unknown>;
}

/** Foundry treats special resources as upgrades during the applyAEs phase. */
function onApplySpecialResource(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  if (!rule.test(state)) {
    rule.ignored = true;
    return;
  }
  const data = rule.data as unknown as SpecialResourceData;
  const key = sluggifyCamel(data.slug);
  const max = Number(resolveValue(rule, state, data.max));
  if (rule.ignored) return;
  const resources = state.actor.system.resources;
  const existing = resources[key];
  if (!(key in state.synthetics.resources)) {
    const level = data.level === null ? null : Number(resolveValue(rule, state, data.level, null, { warn: false }));
    const record = { ...(typeof existing === "object" && existing !== null ? existing : {}), value: 0, max };
    resources[key] = record;
    state.synthetics.resources[key] = {
      slug: data.slug,
      label: state.catalogue?.localize(rule.label) ?? rule.label,
      value: 0,
      max,
      itemUuid: data.itemUuid,
      level: typeof level === "number" && Number.isFinite(level) ? level : null,
      renew: data.renew,
      source: rule.item.uuid,
      sourceName: rule.item.name,
    };
  } else if (typeof existing === "object" && existing !== null && "max" in existing) {
    const current = existing as { max: number };
    current.max = Math.floor(Math.max(max, current.max ?? 0));
    state.synthetics.resources[key]!.max = current.max;
  }
}

function beforeSpecialResource(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as SpecialResourceData;
  const key = sluggifyCamel(data.slug);
  const existing = state.actor.system.resources[key];
  const synthetic = state.synthetics.resources[key];
  if (!existing || typeof existing !== "object" || !("max" in existing) || !synthetic) return rule.fail(`Missing resource system data for resource ${data.slug}`);
  const record = existing as { value: number; max: number };
  const max = Math.floor(record.max ?? 0);
  record.max = max;
  const raw = data.value ?? max;
  record.value = Math.min(raw, max);
  synthetic.max = max;
  synthetic.value = record.value;
}

export const SpecialResourceHandler: RuleHandler = { key: "SpecialResource", parse: parseSpecialResource, onApplyActiveEffects: onApplySpecialResource, beforePrepareData: beforeSpecialResource };

/* ---------------------------- DexterityModifierCap ---------------------------- */

function parseDexCap(rule: RuleInstance): void {
  const value = cleanRuleValue(rule.source.value);
  if (value === undefined) return rule.fail("value: may not be undefined");
  rule.data = { value };
}

function beforeDexCap(rule: RuleInstance, state: EngineState): void {
  if (!rule.test(state)) return;
  const value = resolveValue(rule, state, rule.data.value);
  if (rule.ignored) return;
  if (typeof value !== "number") return rule.fail("value must be a number");
  state.synthetics.dexterityModifierCaps.push({ value, source: state.catalogue?.localize(rule.label) ?? rule.label });
}

export const DexterityModifierCapHandler: RuleHandler = { key: "DexterityModifierCap", parse: parseDexCap, beforePrepareData: beforeDexCap };

export { resolveInjectedProperties };
